import { useCallback, useEffect, useRef, useState } from "react";

const POLL_INTERVAL_MS = 5 * 60 * 1000;

async function fetchBuildId() {
  const base = process.env.PUBLIC_URL || "";
  const url = `${base}/version.json?t=${Date.now()}`;

  const response = await fetch(url, {
    cache: "no-store",
    headers: {
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
    },
  });

  if (!response.ok) return null;

  const data = await response.json();
  return data.buildId || data.version || null;
}

export function useAppVersion() {
  const loadedVersionRef = useRef(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  const checkForUpdate = useCallback(async () => {
    if (process.env.NODE_ENV !== "production") return;

    try {
      const remoteVersion = await fetchBuildId();
      if (!remoteVersion) return;

      if (loadedVersionRef.current === null) {
        loadedVersionRef.current = remoteVersion;
        return;
      }

      if (remoteVersion !== loadedVersionRef.current) {
        setUpdateAvailable(true);
      }
    } catch {
      // Ignore network errors during background version checks.
    }
  }, []);

  useEffect(() => {
    if (process.env.NODE_ENV !== "production") return undefined;

    checkForUpdate();

    const intervalId = window.setInterval(checkForUpdate, POLL_INTERVAL_MS);
    const onFocus = () => checkForUpdate();
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkForUpdate();
      }
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [checkForUpdate]);

  const reload = useCallback(() => {
    window.location.reload();
  }, []);

  return { updateAvailable, reload };
}
